import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getCollegeList, searchCollege } from '../../service/cms.service';
import CustomCollegeInput from './CustomCollegeInput';

export default function CollegeSelectionBox({ onChange, error, usedFrom }) {
  // College List
  const [collegeList, setCollegeList] = useState([]);
  // College offset
  const [collegeOffset, setCollegeOffset] = useState(0);
  // College search text
  const [searchText, setSearchText] = useState('');
  // Data loading
  const [dataLoading, setDataLoading] = useState(true);
  // Has more
  const [hasMore, setHasMore] = useState(false);
  // take the scroll position at the moment
  const [position, setPosition] = useState(0);
  const [listboxNode, setListboxNode] = useState('');
  // Selected college
  const [selectedCollege, setSelectedCollege] = useState(null);
  // Custom college
  const [customCollege, setCustomCollege] = useState(null);
  // College limit
  const collegeLimit = 50;

  // This is defined option which at first place in list.
  const otherObj = {
    id: nanoid(),
    name: 'Other'
  };

  // Set unique items only
  const setUniqueCollege = (list) => {
    const uniqueList = [...collegeList];
    list.forEach((lt) => {
      const findIndex = uniqueList.findIndex((ul) => ul.id === lt.id);
      if (findIndex === -1) {
        uniqueList.push(lt);
      }
    });
    setCollegeList([...uniqueList]);
  };

  // When user search anything then this function will called and make api call
  useEffect(() => {
    const filterCollege = async () => {
      const res = await searchCollege(searchText, collegeLimit, 0);
      if (res) {
        setUniqueCollege([otherObj, ...res]);
        setHasMore(res.length === 50);
        setCollegeOffset(50);
        setDataLoading(false);
      }
    };

    if (searchText !== '') filterCollege();
  }, [searchText]);

  // When selecting college or any custom name name is added then this will called
  // According to state of selected college the parent will notify
  useEffect(() => {
    if (selectedCollege && selectedCollege?.name.toLowerCase() === 'other') {
      onChange(customCollege);
      return;
    }

    if (selectedCollege) {
      onChange(selectedCollege);
    }

    if (selectedCollege === null) {
      onChange(null);
    }
  }, [selectedCollege, customCollege]);

  // This is used for positioning the scrollbar in auto complete while infinite scrolling
  useEffect(() => {
    // this condition checks if there is a necessary DOM Node
    if (listboxNode !== '') {
      listboxNode.scrollTop = position;
    }
    // it will only work when the position or node changes
  }, [position, listboxNode]);

  async function getColleges() {
    const res = await getCollegeList(collegeLimit, 0);
    if (res) {
      setUniqueCollege([otherObj, ...res]);
      setHasMore(res.length === 50);
      setCollegeOffset((prev) => prev + 50);
      setDataLoading(false);
    } else {
      setDataLoading(false);
    }
  }

  // This will called first time when component is mounted
  useEffect(() => {
    getColleges();
  }, []);

  const loadMoreItems = async (event) => {
    setListboxNode(event.currentTarget);

    const x = listboxNode.scrollTop + listboxNode.clientHeight;

    // only when checking this condition we change the position
    if (listboxNode.scrollHeight - x <= 1 && hasMore) {
      let res = [];
      if (searchText) {
        res = await searchCollege(searchText, collegeLimit, collegeOffset);
      } else {
        res = await getCollegeList(collegeLimit, collegeOffset);
      }
      if (res) {
        setPosition(x);
        setUniqueCollege(res);
        setHasMore(res.length === 50);
        setCollegeOffset((prev) => prev + 50);
      }
    }
  };

  return (
    <>
      <Autocomplete
        options={collegeList}
        loading={dataLoading}
        getOptionLabel={(option) => option.name}
        fullWidth
        freeSolo
        sx={{
          marginBottom:
            (usedFrom !== 'profile' && selectedCollege?.name.toLowerCase()) === 'other' ? '20px' : 0
        }}
        onChange={(e, v) => {
          if (v)
            setSelectedCollege({
              name: v?.name.trim(),
              id: v.id,
              type: v.is_school ? 'school' : 'college'
            });
          else {
            setSelectedCollege(null);
            setCustomCollege(null);
          }
        }}
        onInputChange={(e) => {
          if (e?.target?.value === undefined && searchText !== '') {
            setDataLoading(true);
            setCollegeList([]);
            setSearchText('');
            getColleges();
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{ m: 0 }}
            margin="dense"
            value={searchText}
            label="Select School/College"
            variant="outlined"
            error={!selectedCollege && error}
            helperText={!selectedCollege && error?.message}
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDataLoading(true);
              setCollegeList([]);
              setSearchText(e.target.value);
            }}
            InputProps={{
              ...params.InputProps,
              autoComplete: 'new-password',
              endAdornment: (
                <>
                  {dataLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        ListboxProps={{
          onScroll: loadMoreItems
        }}
      />

      {/* If user select other then this invokes */}
      {selectedCollege?.name.toLowerCase() === 'other' && (
        <CustomCollegeInput onChange={(e) => setCustomCollege(e)} error={error} />
      )}
    </>
  );
}

CollegeSelectionBox.defaultProps = {
  usedFrom: ''
};

CollegeSelectionBox.propTypes = {
  onChange: PropTypes.func,
  error: PropTypes.any,
  usedFrom: PropTypes.string
};
